import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup} from '@angular/forms';
import {Observable, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {Select, Store} from '@ngxs/store';
import {PageEvent} from '@angular/material/paginator';
import {Invoice} from '@shared/models/invoice/invoice.model';
import {EntityMap} from '@shared/models/types';
import {AuthState} from '@core/store/auth/auth.state';
import {User} from '@core/models/user.model';
import {DialogHandlerService} from '@core/services/dialog-handler.service';
import {InvoiceState} from '../../store/invoice/invoice.state';
import {PageInvoiceAbleJobs} from '../../models/pagiable-invoice-able-jobs.model';
import {PageReadyToInvoice} from '../../models/pagiable-ready-to-invoice.model';
import {ReadyToInvoice} from '../../models/ready-to-invoice';
import {RateSheetType} from '@shared/models/enums/rate-sheet-type.enum';
import {InvoiceType} from '../../models/enums/invoice-type.enum';
import {PaymentType} from '../../models/enums/payment-type.enum';
import {Expense} from '../../models/expense.model';
import {Cost} from '../../models/cost.model';
import {Tax} from '../../models/tax.model';
import {
  AddLoadsToInvoiceAction,
  ClearAllCompletedLoadsForSelectionAction,
  GetAllCompletedLoadsForSelectionAction,
  GetInvoiceAbleJobsAction,
  GetLoadsByJobFileAndCompletedAction,
  UpdateReadyToInvoiceAction
} from '../../store/invoice/invoice.actions';
import {AddCostComponent} from '../add-cost/add-cost.component';
import {AddAdditionAndSubtractionComponent} from '../add-addition-and-subtraction/add-addition-and-subtraction.component';
import {AdditionAndSubtraction} from '../../models/addition-and-subtraction.model';
import {RtiExtraServiceService} from '../../services/rti-extra-service.service';

interface AddLoadDialogData {
  invoice: Invoice;
}

@Component({
  selector: 'hmt-add-load-dialog',
  templateUrl: './add-load-dialog.component.html',
  styleUrls: ['./add-load-dialog.component.scss'],
  standalone: false
})
export class AddLoadDialogComponent implements OnInit, OnDestroy {
  @Select(InvoiceState.getJobsForCreatedTo) jobsForCustomer$: Observable<EntityMap<string, PageInvoiceAbleJobs>>;
  @Select(InvoiceState.getCompletedLoadsForJob) completedLoadsForJob$: Observable<EntityMap<string, PageReadyToInvoice>>;
  @Select(InvoiceState.getAllCompletedLoadsForSelection) allLoadsForSelection$: Observable<EntityMap<string, ReadyToInvoice[]>>;
  @Select(InvoiceState.getAllTaxes) taxes$: Observable<Tax[]>;
  @Select(AuthState.getUser) user$: Observable<User>;

  invoice: Invoice;
  createdTo: string;
  invoiceType: InvoiceType;
  user: User;

  filtersForm: FormGroup;

  jobsPage: PageInvoiceAbleJobs;
  loadsByJob: EntityMap<string, PageReadyToInvoice> = {};
  expandedJobIds: Set<string> = new Set<string>();
  expandedJpmIds: Set<string> = new Set<string>();

  selectedJpmIds: Set<string> = new Set<string>();
  selectedJobIds: Set<string> = new Set<string>();
  knownJobIds: Set<string> = new Set<string>();
  selectAllLoading = false;

  pageNo = 0;
  pageSize = 5;
  pageSizeOptions: number[] = [5, 10, 25, 100];

  paymentType = PaymentType;

  private readonly DEFAULT_FILTER_TYPES: string[] = ['CREATED', 'STARTED', 'COMPLETED'];

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    public dialogRef: MatDialogRef<AddLoadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddLoadDialogData,
    private store$: Store,
    public dialogHandlerService: DialogHandlerService,
    public rtiExtraServiceService: RtiExtraServiceService
  ) {
    this.invoice = data.invoice;
    this.createdTo = data.invoice?.createdTo;
    this.invoiceType = data.invoice?.type ?? InvoiceType.RECEIVABLE;
  }

  ngOnInit(): void {
    const {fromDate, toDate} = this.buildDefaultDateRange();
    this.filtersForm = new FormGroup({
      searchKey: new FormControl(''),
      fromDate: new FormControl<Date | null>(fromDate),
      toDate: new FormControl<Date | null>(toDate)
    });

    this.user$.pipe(takeUntil(this.unsubscribe), tap(u => this.user = u)).subscribe();

    this.jobsForCustomer$.pipe(
      takeUntil(this.unsubscribe),
      tap(map => {
        this.jobsPage = map?.[this.createdTo];
        (this.jobsPage?.content || []).forEach(j => this.knownJobIds.add(j.jobRefId));
      })
    ).subscribe();

    this.completedLoadsForJob$.pipe(
      takeUntil(this.unsubscribe),
      tap(map => {
        this.loadsByJob = map || {};
        this.selectedJobIds.forEach(jobRefId => {
          const loads = this.loadsByJob?.[jobRefId]?.content || [];
          loads.forEach(l => {
            if (this.isLoadSelectable(l)) { this.selectedJpmIds.add(l.jpmId); }
          });
        });
      })
    ).subscribe();

    this.allLoadsForSelection$.pipe(
      takeUntil(this.unsubscribe),
      tap(map => {
        const all = map?.[this.createdTo];
        if (!all) { return; }
        this.applyAllLoadsSelection(all);
        this.selectAllLoading = false;
      })
    ).subscribe();

    this.filtersForm.get('searchKey').valueChanges.pipe(
      takeUntil(this.unsubscribe),
      debounceTime(400),
      distinctUntilChanged(),
      tap(() => {
        this.pageNo = 0;
        this.invalidateCrossPageSelection();
        this.fetchJobs();
      })
    ).subscribe();

    this.filtersForm.get('fromDate').valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.pageNo = 0;
        this.invalidateCrossPageSelection();
        this.fetchJobs();
      })
    ).subscribe();

    this.filtersForm.get('toDate').valueChanges.pipe(
      takeUntil(this.unsubscribe),
      tap(() => {
        this.pageNo = 0;
        this.invalidateCrossPageSelection();
        this.fetchJobs();
      })
    ).subscribe();

    this.fetchJobs();
  }

  ngOnDestroy(): void {
    if (this.createdTo) {
      this.store$.dispatch(new ClearAllCompletedLoadsForSelectionAction(this.createdTo));
    }
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private fetchJobs() {
    if (!this.user?.orgId) {
      this.user$.pipe(
        takeUntil(this.unsubscribe),
        tap(u => {
          this.user = u;
          this.dispatchFindJobs();
        })
      ).subscribe();
      return;
    }
    this.dispatchFindJobs();
  }

  private dispatchFindJobs() {
    const {searchKey, fromDate, toDate} = this.filtersForm.value;
    const defaults = this.buildDefaultDateRange();
    this.store$.dispatch(new GetInvoiceAbleJobsAction(
      this.user.orgId,
      this.createdTo,
      this.pageNo,
      this.pageSize,
      searchKey || '',
      this.invoiceType,
      fromDate || defaults.fromDate,
      toDate || defaults.toDate,
      this.DEFAULT_FILTER_TYPES
    ));
  }

  private buildDefaultDateRange(): {fromDate: Date; toDate: Date} {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    return {fromDate, toDate};
  }

  toggleJob(jobRefId: string) {
    if (this.expandedJobIds.has(jobRefId)) {
      this.expandedJobIds.delete(jobRefId);
    } else {
      this.expandedJobIds.add(jobRefId);
      this.fetchLoadsForJob(jobRefId);
    }
  }

  isJobExpanded(jobRefId: string): boolean {
    return this.expandedJobIds.has(jobRefId);
  }

  private fetchLoadsForJob(jobRefId: string) {
    this.store$.dispatch(new GetLoadsByJobFileAndCompletedAction(
      this.user.orgId,
      this.createdTo,
      jobRefId,
      true,
      0,
      10,
      this.invoiceType
    ));
  }

  isLoadAlreadyInInvoice(load: ReadyToInvoice): boolean {
    return (this.invoice?.loads || []).some(l => l.jpmId === load.jpmId);
  }

  isLoadSelectable(load: ReadyToInvoice): boolean {
    if (this.isLoadAlreadyInInvoice(load)) { return false; }
    return !!load?.rateCardCalculationRequest?.rateSheet;
  }

  isLoadSelected(jpmId: string): boolean {
    return this.selectedJpmIds.has(jpmId);
  }

  toggleLoad(load: ReadyToInvoice, checked: boolean) {
    if (!this.isLoadSelectable(load)) {
      return;
    }
    if (checked) {
      this.selectedJpmIds.add(load.jpmId);
      const loads = this.loadsByJob?.[load.jobRefId]?.content || [];
      const allSelected = loads
        .filter(l => this.isLoadSelectable(l))
        .every(l => this.selectedJpmIds.has(l.jpmId));
      if (allSelected) { this.selectedJobIds.add(load.jobRefId); }
    } else {
      this.selectedJpmIds.delete(load.jpmId);
      this.selectedJobIds.delete(load.jobRefId);
    }
  }

  toggleLoadExpand(jpmId: string) {
    if (this.expandedJpmIds.has(jpmId)) {
      this.expandedJpmIds.delete(jpmId);
    } else {
      this.expandedJpmIds.add(jpmId);
    }
  }

  isLoadExpanded(jpmId: string): boolean {
    return this.expandedJpmIds.has(jpmId);
  }

  toggleJobSelection(jobRefId: string, checked: boolean) {
    if (checked) {
      this.selectedJobIds.add(jobRefId);
      const loaded = this.loadsByJob?.[jobRefId]?.content;
      if (loaded?.length) {
        loaded.forEach(l => {
          if (this.isLoadSelectable(l)) { this.selectedJpmIds.add(l.jpmId); }
        });
      } else {
        this.fetchLoadsForJob(jobRefId);
      }
    } else {
      this.selectedJobIds.delete(jobRefId);
      const loaded = this.loadsByJob?.[jobRefId]?.content || [];
      loaded.forEach(l => this.selectedJpmIds.delete(l.jpmId));
    }
  }

  isJobSelected(jobRefId: string): boolean {
    if (this.selectedJobIds.has(jobRefId)) { return true; }
    const loads = this.loadsByJob?.[jobRefId]?.content || [];
    const selectable = loads.filter(l => this.isLoadSelectable(l));
    return selectable.length > 0 && selectable.every(l => this.selectedJpmIds.has(l.jpmId));
  }

  isJobPartiallySelected(jobRefId: string): boolean {
    if (this.isJobSelected(jobRefId)) { return false; }
    const loads = this.loadsByJob?.[jobRefId]?.content || [];
    const selectable = loads.filter(l => this.isLoadSelectable(l));
    const selected = selectable.filter(l => this.selectedJpmIds.has(l.jpmId)).length;
    return selected > 0 && selected < selectable.length;
  }

  toggleSelectAllVisible(checked: boolean) {
    if (checked) {
      const {searchKey, fromDate, toDate} = this.filtersForm.value;
      const defaults = this.buildDefaultDateRange();
      this.selectAllLoading = true;
      this.store$.dispatch(new GetAllCompletedLoadsForSelectionAction(
        this.user.orgId,
        this.createdTo,
        searchKey || '',
        this.invoiceType,
        fromDate || defaults.fromDate,
        toDate || defaults.toDate,
        this.DEFAULT_FILTER_TYPES
      ));
    } else {
      this.clearAllSelections();
      this.store$.dispatch(new ClearAllCompletedLoadsForSelectionAction(this.createdTo));
    }
  }

  isAllVisibleSelected(): boolean {
    const ids = Array.from(this.knownJobIds);
    if (!ids.length) { return false; }
    return ids.every(id => this.isJobSelected(id));
  }

  isAllVisiblePartiallySelected(): boolean {
    const ids = Array.from(this.knownJobIds);
    if (!ids.length) { return false; }
    const sel = ids.filter(id => this.isJobSelected(id) || this.isJobPartiallySelected(id)).length;
    return sel > 0 && !this.isAllVisibleSelected();
  }

  private clearAllSelections() {
    this.selectedJobIds.clear();
    this.selectedJpmIds.clear();
  }

  private invalidateCrossPageSelection() {
    this.clearAllSelections();
    this.knownJobIds.clear();
    this.selectAllLoading = false;
    this.store$.dispatch(new ClearAllCompletedLoadsForSelectionAction(this.createdTo));
  }

  private applyAllLoadsSelection(loads: ReadyToInvoice[]) {
    const grouped: EntityMap<string, ReadyToInvoice[]> = {};
    loads.forEach(l => {
      if (!grouped[l.jobRefId]) { grouped[l.jobRefId] = []; }
      grouped[l.jobRefId].push(l);
    });

    Object.keys(grouped).forEach(jobRefId => {
      this.knownJobIds.add(jobRefId);
      this.selectedJobIds.add(jobRefId);
      grouped[jobRefId].forEach(l => {
        if (this.isLoadSelectable(l)) { this.selectedJpmIds.add(l.jpmId); }
      });

      const existing = this.loadsByJob?.[jobRefId];
      const merged: ReadyToInvoice[] = existing?.content?.length
        ? this.mergeLoads(existing.content, grouped[jobRefId])
        : grouped[jobRefId];

      this.loadsByJob = {
        ...this.loadsByJob,
        [jobRefId]: {
          ...(existing || {}),
          content: merged,
          page: existing?.page || {totalElements: merged.length, pageSize: merged.length, pageNumber: 0}
        } as PageReadyToInvoice
      };
    });
  }

  private mergeLoads(existing: ReadyToInvoice[], incoming: ReadyToInvoice[]): ReadyToInvoice[] {
    const byId = new Map<string, ReadyToInvoice>();
    existing.forEach(l => byId.set(l.jpmId, l));
    incoming.forEach(l => byId.set(l.jpmId, l));
    return Array.from(byId.values());
  }

  handlePage(event: PageEvent) {
    this.pageNo = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetchJobs();
  }

  reset() {
    const {fromDate, toDate} = this.buildDefaultDateRange();
    this.filtersForm.reset({searchKey: '', fromDate, toDate}, {emitEvent: false});
    this.selectedJpmIds.clear();
    this.selectedJobIds.clear();
    this.expandedJobIds.clear();
    this.expandedJpmIds.clear();
    this.knownJobIds.clear();
    this.selectAllLoading = false;
    this.store$.dispatch(new ClearAllCompletedLoadsForSelectionAction(this.createdTo));
    this.pageNo = 0;
    this.fetchJobs();
  }

  hasSelection(): boolean {
    return this.selectedJpmIds.size > 0;
  }

  selectedCount(): number {
    return this.selectedJpmIds.size;
  }

  add() {
    if (!this.hasSelection()) { return; }
    this.store$.dispatch(new AddLoadsToInvoiceAction(
      this.invoice._id,
      Array.from(this.selectedJpmIds),
      this.invoiceType
    )).subscribe(() => {
      this.dialogRef.close({added: Array.from(this.selectedJpmIds)});
    });
  }

  close() {
    this.dialogRef.close();
  }

  applyCurrency(value: number): string {
    const code = this.invoice?.currency?.currentCurrency || '';
    const rate = this.invoice?.currency?.currencyRate || 1;
    const formatted = (value / rate).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return `${code} ${formatted}`;
  }

  isWeightBasedLoad(load: ReadyToInvoice): boolean {
    return load?.rateCardCalculationRequest?.rateSheet?.type === RateSheetType.WEIGHT_BASED;
  }

  weightFreightSubLine(load: ReadyToInvoice): string {
    const request = load?.rateCardCalculationRequest;
    const rateSheet = request?.rateSheet;
    if (!rateSheet) {
      return '';
    }
    const weightInLoadKg = request?.weightInLoadKg ?? 0;
    const ratioKeyByUnit: { [unit: string]: string } = {
      KG: 'kg', G: 'g', LBS: 'lb', VISS: 'viss', TONNE: 'tonne',
    };
    const ratioKey = ratioKeyByUnit[rateSheet.rateUnit ?? ''] ?? 'kg';
    const ratioToKg = ratioKey === 'kg' ? 1 : (rateSheet.weightConversionRatios?.[ratioKey] || 1);
    const weightInBillingUnit = ratioToKg ? weightInLoadKg / ratioToKg : weightInLoadKg;
    const unit = (rateSheet.rateUnit || '').toLowerCase();
    return `${weightInLoadKg} kg → ${weightInBillingUnit.toFixed(2)} ${unit} × `
      + `${this.applyCurrency(rateSheet.ratePerUnit || 0)}/${unit}`;
  }

  loadTotal(load: ReadyToInvoice): number {
    if (!load) { return 0; }
    return this.rtiExtraServiceService.calculateSubTotal(load) || load.loadCost || 0;
  }

  getBreadcrumb(locations: string[] = []): string[] {
    if (!locations || locations.length === 0) { return []; }
    if (locations.length === 1) { return [locations[0]]; }
    return [locations[0], locations[locations.length - 1]];
  }

  private convertToDefaultCurrency(value: number): number {
    const currency = this.invoice?.currency;
    if (!currency) { return value; }
    return currency.currentCurrency !== currency.defaultCurrency
      ? value * (currency.currencyRate || 1)
      : value;
  }

  addExpense(load: ReadyToInvoice, existing?: Expense) {
    const dialogData: Cost = existing ? {
      id: existing.id,
      value: existing.amount,
      description: existing.costDescription
    } : null;
    const dialogRef = this.dialogHandlerService.openDialog(AddCostComponent, {cost: dialogData}, {
      height: '350px',
      width: '30%',
      panelClass: 'custom-dialog-container',
      position: {top: '10%', left: '35%'},
    });

    dialogRef.componentInstance.addCost.pipe(
      takeUntil(this.unsubscribe),
      tap((cost: Cost) => {
        cost.value = this.convertToDefaultCurrency(cost.value);
        const costData: Expense = {
          id: cost.id,
          amount: cost.value,
          paymentType: PaymentType.AMOUNT,
          costDescription: cost.description,
          feeType: existing?.feeType
        };
        const expenses = existing
          ? (load.expenses || []).map(e => e.id === costData.id ? costData : e)
          : [...(load.expenses || []), costData];
        const updated: ReadyToInvoice = {...load, expenses};
        this.persistReadyToInvoice(updated);
      })
    ).subscribe();
  }

  removeExpense(load: ReadyToInvoice, id: string) {
    const expenses = (load.expenses || []).filter(e => e.id !== id);
    this.persistReadyToInvoice({...load, expenses});
  }

  addAddedItem(load: ReadyToInvoice, existing?: Expense) {
    const data = {
      paymentType: existing?.paymentType ?? PaymentType.AMOUNT,
      additionAndSubtraction: existing ? {
        id: existing.id,
        value: existing.amount,
        description: existing.costDescription,
        paymentType: existing.paymentType
      } as AdditionAndSubtraction : null,
      type: 'discount',
      taxes: this.taxes$,
      name: 'Add Fee'
    };

    const dialogRef = this.dialogHandlerService.openDialog(AddAdditionAndSubtractionComponent, data, {
      height: '350px',
      width: '30%',
      panelClass: 'custom-dialog-container',
      position: {top: '10%', left: '35%'},
    });

    dialogRef.componentInstance.addAdditionAndSubtraction.pipe(
      takeUntil(this.unsubscribe),
      tap((value: AdditionAndSubtraction) => {
        const amount = value.paymentType === PaymentType.AMOUNT
          ? this.convertToDefaultCurrency(value.value)
          : value.value;
        const expense: Expense = {
          id: value.id,
          amount,
          paymentType: value.paymentType,
          costDescription: value.description
        };
        const list = load.addedOtherCost || [];
        const updated = existing
          ? list.map(e => e.id === expense.id ? expense : e)
          : [...list, expense];
        this.persistReadyToInvoice({...load, addedOtherCost: updated});
      })
    ).subscribe();
  }

  removeAddedItem(load: ReadyToInvoice, id: string) {
    const addedOtherCost = (load.addedOtherCost || []).filter(e => e.id !== id);
    this.persistReadyToInvoice({...load, addedOtherCost});
  }

  addDiscount(load: ReadyToInvoice) {
    const existing = load.addedDiscount;
    const data = {
      paymentType: existing?.paymentType ?? PaymentType.AMOUNT,
      additionAndSubtraction: existing ? {
        id: existing.id,
        value: existing.amount,
        description: existing.costDescription,
        paymentType: existing.paymentType
      } as AdditionAndSubtraction : null,
      type: 'discount',
      taxes: this.taxes$,
      name: 'Add Discount'
    };

    const dialogRef = this.dialogHandlerService.openDialog(AddAdditionAndSubtractionComponent, data, {
      height: '350px',
      width: '30%',
      panelClass: 'custom-dialog-container',
      position: {top: '10%', left: '35%'},
    });

    dialogRef.componentInstance.addAdditionAndSubtraction.pipe(
      takeUntil(this.unsubscribe),
      tap((value: AdditionAndSubtraction) => {
        const amount = value.paymentType === PaymentType.AMOUNT
          ? this.convertToDefaultCurrency(value.value)
          : value.value;
        const expense: Expense = {
          id: value.id,
          amount,
          paymentType: value.paymentType,
          costDescription: value.description
        };
        this.persistReadyToInvoice({...load, addedDiscount: expense});
      })
    ).subscribe();
  }

  removeDiscount(load: ReadyToInvoice) {
    this.persistReadyToInvoice({...load, addedDiscount: null});
  }

  private persistReadyToInvoice(updated: ReadyToInvoice) {
    this.store$.dispatch(new UpdateReadyToInvoiceAction(updated));
    const jobRefId = updated.jobRefId;
    const current = this.loadsByJob?.[jobRefId];
    if (current?.content) {
      const content = current.content.map(l => l.jpmId === updated.jpmId ? updated : l);
      this.loadsByJob = {...this.loadsByJob, [jobRefId]: {...current, content}};
    }
  }

  trackByJpmId(_index: number, load: ReadyToInvoice): string {
    return load?.jpmId;
  }

  trackByJobRefId(_index: number, job: any): string {
    return job?.jobRefId;
  }
}
